import { fetchComRetry } from './httpRetry';

const API_BASE = process.env.REACT_APP_AUTH_API_BASE || 'https://api.leveragesec.com.br';

export class FluxoCaixaApiError extends Error {
  constructor(message, { status } = {}) {
    super(message);
    this.name = 'FluxoCaixaApiError';
    this.status = status;
  }
}

async function apiPost(url, body, comoConvidado) {
  let res;
  try {
    res = await fetchComRetry(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(comoConvidado ? { 'X-Visualizar-Como': 'convidado' } : {}),
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new FluxoCaixaApiError('Não foi possível conectar ao servidor.', { status: 0 });
  }
  if (!res.ok) throw new FluxoCaixaApiError('Erro ao buscar dados financeiros.', { status: res.status });
  return res.json();
}

function buildBody(emissaoIds, dataInicio, dataFim) {
  const body = { emissao_ids: emissaoIds, real_time: false };
  if (dataInicio != null) body.data_inicio = dataInicio;
  if (dataFim != null) body.data_fim = dataFim;
  return body;
}

// Cache de dados por emissão — persiste entre trocas de aba sem re-fetch.
// Armazena { cal, pu, full } indexado por emissao_id (string). `full` indica se
// o valor guardado é o histórico completo (true) ou só uma janela rápida (false),
// usada pro carregamento progressivo: a tela libera com a janela e o histórico
// completo chega depois, em segundo plano, sem travar a primeira renderização.
// Só cacheamos quando o calendário (dado primário) retornou com sucesso;
// falha do PU diário não impede cache, apenas marca pu: null.
//
// Chave prefixada com "g:" quando `comoConvidado` (toggle "visão pública" de
// Detalhe2.jsx) — namespace separado do cache real do usuário Leverage, senão
// o recorte de convidado vazaria pra a navegação normal (e vice-versa) ao
// alternar o toggle.
const _cache = new Map();
const cacheKey = (emissaoId, comoConvidado) => (comoConvidado ? `g:${emissaoId}` : String(emissaoId));

export function peekFluxoCaixaCache(emissaoId, comoConvidado) {
  return _cache.get(cacheKey(emissaoId, comoConvidado)) || null;
}

export function clearFluxoCaixaCache() {
  _cache.clear();
  _fullInFlight.clear();
}

function setCache(key, entry) {
  const prev = _cache.get(key);
  if (prev?.full && !entry.full) return; // nunca regride completo -> parcial
  _cache.set(key, entry);
}

async function fetchCalPu(emissaoId, dataInicio, dataFim, comoConvidado) {
  const id = String(emissaoId);
  const [calResult, puResult] = await Promise.allSettled([
    postCalendarioPagamentos([emissaoId], dataInicio, dataFim, comoConvidado),
    postPUDiario([emissaoId], dataInicio, dataFim, comoConvidado),
  ]);
  const cal    = calResult.status === 'fulfilled' ? (calResult.value[id] || null) : null;
  const pu     = puResult.status  === 'fulfilled' ? (puResult.value[id]  || null) : null;
  const calErr = calResult.status === 'rejected'  ? (calResult.reason?.message || 'Erro ao carregar calendário.') : null;
  const puErr  = puResult.status  === 'rejected'  ? (puResult.reason?.message  || 'Erro ao carregar histórico diário.') : null;
  return { cal, pu, calErr, puErr };
}

// Busca uma janela de datas (rápida) — usada pra liberar a tela sem esperar o
// histórico inteiro. Grava no cache como parcial (full: false).
export async function fetchFluxoCaixaWindow(emissaoId, dataInicio, dataFim, comoConvidado) {
  const result = await fetchCalPu(emissaoId, dataInicio, dataFim, comoConvidado);
  if (!result.calErr) setCache(cacheKey(emissaoId, comoConvidado), { cal: result.cal, pu: result.pu, full: false });
  return result;
}

// Busca o histórico completo (sem corte de data), sempre em paralelo com
// degradação graceful (se um endpoint falhar o outro ainda é exibido).
// Deduplica chamadas em voo pro mesmo emissaoId — evita duas buscas completas
// concorrentes se o efeito de carregamento rodar mais de uma vez (StrictMode,
// troca rápida de aba), ou se quem exporta precisar "esperar" uma busca que o
// carregamento progressivo já iniciou.
const _fullInFlight = new Map();

export function fetchAndCacheFluxoCaixa(emissaoId, comoConvidado) {
  const key = cacheKey(emissaoId, comoConvidado);
  if (_fullInFlight.has(key)) return _fullInFlight.get(key);
  const promise = fetchCalPu(emissaoId, undefined, undefined, comoConvidado).then(result => {
    if (!result.calErr) setCache(key, { cal: result.cal, pu: result.pu, full: true });
    return result;
  });
  _fullInFlight.set(key, promise);
  promise.finally(() => { _fullInFlight.delete(key); });
  return promise;
}

// Retorna { [emissao_id]: { series: [...] } }
// IDs sem dados são omitidos da resposta pelo backend (sempre HTTP 200).
export function postCalendarioPagamentos(emissaoIds, dataInicio, dataFim, comoConvidado) {
  return apiPost(`${API_BASE}/v1/calendario-pagamentos`, buildBody(emissaoIds, dataInicio, dataFim), comoConvidado);
}

export function postPUDiario(emissaoIds, dataInicio, dataFim, comoConvidado) {
  return apiPost(`${API_BASE}/v1/pu-diario`, buildBody(emissaoIds, dataInicio, dataFim), comoConvidado);
}

export function postCalendarioIntegralizacoes(emissaoIds, dataInicio, dataFim, comoConvidado) {
  return apiPost(`${API_BASE}/v1/calendario-integralizacoes`, buildBody(emissaoIds, dataInicio, dataFim), comoConvidado);
}

// GET /v1/pu-diario/xlsx — planilha pronta (curva inteira, todos os campos do
// GetPus da Akrual) de UMA série. Só com cookie do portal e chamada vinda do
// site (Origin): link direto/<a href> cai no 403, por isso fetch+blob. Sem
// fetchComRetry: a Akrual já leva alguns segundos e 429 é limite, não falha
// transitória. Erros vêm em JSON, nunca em .xlsx — checa res.ok antes do blob.
const PU_XLSX_ERRO = {
  401: 'Sua sessão expirou. Entre novamente para baixar a planilha.',
  403: 'Download não permitido para este acesso.',
  404: 'Série não encontrada ou sem permissão de acesso.',
  429: 'Muitas solicitações em pouco tempo. Aguarde alguns segundos e tente de novo.',
};

export async function baixarPuDiarioXlsx({ emissaoId, splitId, comoConvidado }) {
  const params = new URLSearchParams({ emissao_id: emissaoId, split_id: splitId });
  let res;
  try {
    res = await fetch(`${API_BASE}/v1/pu-diario/xlsx?${params}`, {
      credentials: 'include',
      ...(comoConvidado ? { headers: { 'X-Visualizar-Como': 'convidado' } } : {}),
    });
  } catch (e) {
    throw new FluxoCaixaApiError('Não foi possível conectar ao servidor.', { status: 0 });
  }
  if (!res.ok) {
    throw new FluxoCaixaApiError(PU_XLSX_ERRO[res.status] || 'Não foi possível gerar a planilha de Preço Unitário.', { status: res.status });
  }
  // filename* (UTF-8) mantém acentos como "1ª"; o ASCII é só fallback.
  const cd = res.headers.get('Content-Disposition') || '';
  const utf8 = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const ascii = cd.match(/filename="([^"]+)"/i);
  const filename = utf8 ? decodeURIComponent(utf8[1]) : ascii ? ascii[1] : `pu_diario_${emissaoId}_${splitId}.xlsx`;
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoga no próximo tick — revogar na hora pode cancelar o download em alguns navegadores.
  setTimeout(() => URL.revokeObjectURL(a.href), 0);
}

// Snapshot da Carteira já cortado por período pelo banco (ver carteiraModel.js/
// carteiraCache.js) — sem data_inicio/data_fim/real_time no corpo: o endpoint
// ignora esses campos (a view resolve os cortes internamente) e o contrato pede
// pra não mandá-los. `comoConvidado` manda o X-Visualizar-Como (toggle "visão
// pública" do Detalhe2), igual aos outros endpoints daqui.
export function postCarteira(emissaoIds, comoConvidado) {
  return apiPost(`${API_BASE}/v1/carteira`, { emissao_ids: emissaoIds }, comoConvidado);
}
